import { useEffect, useRef, useState } from 'react';
import {
  formatLiveClockDisplay,
  formatLiveClockParts,
  type LiveClockState,
} from '@/domain/live-clock';

function useResolvedLiveClockState(
  state: LiveClockState | undefined,
): { state: LiveClockState; now: number } | undefined {
  const [now, setNow] = useState(() => Date.now());
  const tickAnchorRef = useRef<{
    revision: string;
    anchoredAt: number;
    elapsedSeconds: number;
  } | null>(null);

  if (!state?.tickable) {
    tickAnchorRef.current = null;
  } else if (tickAnchorRef.current?.revision !== state.revision) {
    tickAnchorRef.current = {
      revision: state.revision,
      anchoredAt: Date.now(),
      elapsedSeconds: state.elapsedSeconds,
    };
  }

  useEffect(() => {
    if (!state?.tickable) return;
    setNow(Date.now());
    const intervalId = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [state?.revision, state?.tickable]);

  if (!state) return undefined;

  if (!state.tickable || !tickAnchorRef.current) {
    return { state, now };
  }

  return {
    state: {
      ...state,
      anchoredAt: tickAnchorRef.current.anchoredAt,
      elapsedSeconds: tickAnchorRef.current.elapsedSeconds,
    },
    now,
  };
}

/** Formats a live clock and ticks seconds locally when `state.tickable` is true. */
export function useLiveClockDisplay(
  state: LiveClockState | undefined,
  translatePeriod?: (key: string) => string,
): string | undefined {
  const resolved = useResolvedLiveClockState(state);
  if (!resolved) return undefined;
  return formatLiveClockDisplay(resolved.state, resolved.now, translatePeriod);
}

/** Period and time as separate labels for stacked desktop live clock. */
export function useLiveClockParts(
  state: LiveClockState | undefined,
  translatePeriod?: (key: string) => string,
): { period?: string; time?: string } | undefined {
  const resolved = useResolvedLiveClockState(state);
  if (!resolved) return undefined;
  return formatLiveClockParts(resolved.state, resolved.now, translatePeriod);
}
